import React, { useEffect } from "react";
import { useApplication } from "../ApplicationProvider";
import { usePublicConfig } from "../PublicConfigProvider";
import { YesNoRadio } from "../YesNoRadio";
import { FileUpload } from "../FileUpload";
import { TemplateUpload } from "../TemplateUpload";

// Round 16 — Step 2 is now "Eligibility". The basic-info cluster (emails,
// student identity, home address + contact, Family Information) moved up to the
// hard-gated Step 1; the Emergency Contact moved to Step 3. What remains here is
// citizenship/residency (+ proof upload) and the school section (+ certification
// form) — both deliberately free-nav because they depend on documents the
// applicant may not have on hand.
export function Step2BasicInfo({ errors }: { errors: Record<string, string> }) {
  const { data, update } = useApplication();
  const cfg = usePublicConfig();

  const set = <K extends keyof typeof data.step2>(key: K, value: (typeof data.step2)[K]) =>
    update((d) => {
      d.step2[key] = value as never;
    });

  // Graduation year is derived from the session year, not entered by the applicant.
  // Keep stored data in sync (covers drafts saved before this became derived).
  const derivedGraduationYear = cfg.sessionYear + 1;
  useEffect(() => {
    if (data.step2.graduationYear !== derivedGraduationYear) {
      set("graduationYear", derivedGraduationYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedGraduationYear]);

  const genderWord = cfg.program === "boys" ? "Male" : "Female";
  const schoolYearRange = `${cfg.sessionYear - 1}–${cfg.sessionYear}`;
  const otherProgramWord =
    cfg.program === "boys" ? "Boys State or Girls State" : "Girls State or Boys State";

  const showResidencyWarning =
    data.step2.isUSCitizen === "No" && data.step2.isUSLegalResident === "No";

  return (
    <section className="space-y-6">
      <div className="section-card">
        <h2 className="text-xl font-semibold mb-1">Citizenship / Residency</h2>
        <p className="text-sm text-slate-600 mb-4">
          Only US citizens and legal residents of the US are eligible to attend {cfg.programName}.
          Legal visitors (e.g., visa holders, exchange students) are <strong>not</strong> eligible.
        </p>
        <YesNoRadio
          name="isUSCitizen"
          label="Are you a US Citizen?"
          value={data.step2.isUSCitizen}
          onChange={(v) =>
            update((d) => {
              d.step2.isUSCitizen = v;
              // Clear the conditional follow-up if they are a citizen.
              if (v === "Yes") d.step2.isUSLegalResident = null;
            })
          }
          error={errors["isUSCitizen"]}
        />
        {data.step2.isUSCitizen === "No" && (
          <div className="mt-4">
            <YesNoRadio
              name="isUSLegalResident"
              label="Are you a legal resident of the US?"
              value={data.step2.isUSLegalResident}
              onChange={(v) => set("isUSLegalResident", v)}
            />
          </div>
        )}
        {showResidencyWarning && (
          <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3">
            <p className="text-sm text-amber-900">
              <strong>Notice:</strong> All students attending {cfg.programName} must be a US Citizen
              or legal resident of the US.
            </p>
          </div>
        )}
        <div className="mt-4">
          <FileUpload
            fieldKey="proof-of-residency"
            label="Proof of US Citizenship or Legal Residency"
            helper="Acceptable documents include: Birth Certificate, Driver's License with Real ID, Identification Card as Resident Alien, US Passport, or other proof of legal residence or citizenship."
            required
          />
        </div>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-1">School</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="field-label">Current high school name</label>
            <input className="field-input" value={data.step2.highSchoolName} onChange={(e) => set("highSchoolName", e.target.value)} />
            {errors["highSchoolName"] && <p className="field-error">{errors["highSchoolName"]}</p>}
          </div>
          <div>
            <label className="field-label">Anticipated graduation year</label>
            <input className="field-input bg-slate-100 text-slate-700" value={derivedGraduationYear} readOnly disabled />
            <p className="field-hint mt-1">
              Determined automatically for the {cfg.programName} {cfg.sessionYear} session.
            </p>
          </div>
          <div>
            <label className="field-label">GPA</label>
            <input
              type="number"
              step="0.01"
              className="field-input"
              value={data.step2.gpa ?? ""}
              onChange={(e) => set("gpa", e.target.value === "" ? null : Number(e.target.value))}
            />
            {errors["gpa"] && <p className="field-error">{errors["gpa"]}</p>}
          </div>
          <div>
            <label className="field-label">Class rank (optional)</label>
            <input className="field-input" value={data.step2.classRank} onChange={(e) => set("classRank", e.target.value)} />
          </div>
        </div>

        <div className="mt-6 rounded-md border border-program/30 bg-program/5 p-4">
          <p className="text-sm font-semibold text-slate-800 mb-3">
            To be eligible for {cfg.programName} {cfg.sessionYear}, the following must be verified:
          </p>
          <div className="space-y-3">
            <EligibilityCheck
              label={`${genderWord} ${schoolYearRange} high school junior`}
              checked={data.step2.eligibilityJunior}
              onChange={(v) => set("eligibilityJunior", v)}
            />
            <EligibilityCheck
              label="At least 1 semester remaining in senior year"
              checked={data.step2.eligibilitySemesterRemaining}
              onChange={(v) => set("eligibilitySemesterRemaining", v)}
            />
            <EligibilityCheck
              label={`Have not participated in any other ${otherProgramWord} program`}
              checked={data.step2.eligibilityNoOtherProgram}
              onChange={(v) => set("eligibilityNoOtherProgram", v)}
            />
            <EligibilityCheck
              label="Commit to attend the full session"
              checked={data.step2.eligibilityFullSession}
              onChange={(v) => set("eligibilityFullSession", v)}
            />
          </div>
          {errors["eligibility"] && <p className="field-error mt-3">{errors["eligibility"]}</p>}
        </div>

        <div className="mt-4">
          <TemplateUpload
            templateName="school-certification"
            fieldKey="school-certification"
            helper="Download this template, have it completed and signed by your school counselor, then upload the signed form here."
          />
        </div>
      </div>
    </section>
  );
}

function EligibilityCheck({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-program focus:ring-program"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm text-slate-800">{label}</span>
    </label>
  );
}
